import { Bike, MapPin, Wrench } from 'lucide-react'
import type { Event } from '../types/event'
import { IMAGES } from './images'

/**
 * The programme.
 *
 * **Only Friday Bike Night is published.** The client confirmed
 * 2026-09-12 that it is the only event the café actually runs, so the
 * other three are kept here with `published: false` rather than deleted:
 * they were plausible enough to be worth reviving, and holding them
 * keeps the reason for their absence in the data instead of in a commit
 * message. Nothing reads an unpublished event. Registry D41.
 *
 * Friday Bike Night is now fully confirmed: the name and day by the
 * café's own painted sign in `bikeNightSign`, and the 6pm start and
 * year-round recurrence by the client 2026-09-12. It deliberately
 * publishes NO capacity figure — see D46.
 *
 * The capacity figures on the three unpublished events are still
 * invented (I06), and must be checked if any of them is ever published.
 */
export const events: Event[] = [
  {
    id: 'friday-bike-night',
    published: true,
    title: 'Friday Bike Night',
    type: 'ride-in',
    booking: 'walk-in',
    date: 'Every Friday, all year round',
    time: 'From 6pm',
    when: 'Every Friday · from 6pm',
    description:
      "The big one, and it runs every Friday all year round rather than just over the summer. The car park fills up with bikes and the place fills up with the people who rode them. No cover charge, no list, and no need to tell us you're coming. Park up, get yourself a drink and find a spot inside, on the terrace or out on the deck. It carries on into the evening for as long as folk want to sit out.",
    blurb:
      'Every Friday, all year round. The car park fills with bikes. No cover charge, no booking.',
    icon: Bike,
    image: IMAGES.bikeNightSign.src,
    imageAlt: IMAGES.bikeNightSign.alt,
    // No capacity figure on purpose. See D46 and the `spaceNote` field.
    spaceNote: 'Good parking for anyone riding over',
    location: 'Low On Fuel Café, Matlock Road, Brackenfield',
    duration: 'From 6pm into the evening',
  },
  {
    id: 'saturday-ride-in',
    published: false,
    title: 'Saturday Ride-In',
    type: 'ride-in',
    booking: 'walk-in',
    date: 'Every Saturday',
    time: '9am – 1pm',
    when: 'Every Saturday · 9am – 1pm',
    description:
      'The morning version, and a good deal quieter. Riders drift in through the morning on their way out to the Peak District. Park up, get a coffee, have a look over whatever else has turned up, and set off when you feel like it. Classic, cruiser, adventure or sports bike, nobody minds a bit what you arrived on.',
    blurb:
      'Riders drift in through the morning on their way out to the Peaks. All bikes welcome.',
    icon: Bike,
    image: IMAGES.bikeNightCrowd.src,
    imageAlt: IMAGES.bikeNightCrowd.alt,
    capacity: 150,
    spotsLeft: 45,
    location: 'Low On Fuel Café, Matlock Road, Brackenfield',
    duration: '4 hours',
  },
  {
    id: 'peak-district-run',
    published: false,
    title: 'Peak District Run',
    type: 'guided-ride',
    booking: 'bookable',
    date: 'First Sunday of the month',
    time: '10am start',
    when: 'First Sunday monthly · 10am',
    description:
      'The best roads the Peak District has to offer, on a guided group ride. We take in Snake Pass, Cat and Fiddle and Winnats Pass, which are about as rewarding as riding roads get in this country. All abilities welcome, and we go at a comfortable pace with plenty of stops. Back to the café afterwards for your meal and a natter with the rest of the group.',
    blurb:
      'A guided group ride over Snake Pass, Cat and Fiddle and Winnats Pass. All skill levels.',
    icon: MapPin,
    image: IMAGES.helmetsOnTable.src,
    imageAlt: IMAGES.helmetsOnTable.alt,
    price: 35,
    capacity: 25,
    spotsLeft: 8,
    location: 'Starts and ends at Low On Fuel Café',
    duration: '4 – 5 hours',
    mealIncluded: true,
    route: {
      distance: '120 miles',
      estimatedTime: '4 – 5 hours',
      stops: [
        {
          name: 'Low On Fuel Café',
          detail: 'Start point · Briefing and safety check',
        },
        { name: 'Snake Pass summit', detail: '35 miles · Photo stop' },
        {
          name: 'Cat and Fiddle Inn',
          detail: '60 miles · Refreshment break, 30 minutes',
        },
        { name: 'Winnats Pass', detail: '95 miles · Photo stop' },
        { name: 'Low On Fuel Café', detail: '120 miles · Return and meal' },
      ],
    },
  },
  {
    id: 'wrench-night',
    published: false,
    title: 'Wrench Night',
    type: 'community',
    booking: 'bookable',
    date: 'First Wednesday of the month',
    time: '7pm – 10pm',
    when: 'First Wednesday · 7pm – 10pm',
    description:
      'A free workshop night where a few experienced mechanics give up their evening to help other riders out. Bring your bike, your questions and whatever is bothering you. Whether you want a hand with basic maintenance, fancy learning to service your own machine, or just want to talk shop with people who get it, this is the night for it. We put on the space and the company.',
    blurb:
      'Learn basic maintenance, swap tips and talk shop with local mechanics. Free to attend.',
    icon: Wrench,
    image: IMAGES.interiorBar.src,
    imageAlt: IMAGES.interiorBar.alt,
    capacity: 30,
    spotsLeft: 12,
    location: 'Low On Fuel Café',
    duration: '3 hours',
  },
]

/** Everything on the site. The only list any page should render from. */
export function getPublishedEvents(): Event[] {
  return events.filter((event) => event.published)
}

/**
 * Resolves `/whats-on/:slug`. Unpublished events return `undefined` so
 * the route 404s — unlike a sold shop item, a hidden event has no
 * "this one's finished" state worth showing, and a page for something
 * the café does not run is a wrong answer rather than a stale one.
 */
export function getEvent(id: string): Event | undefined {
  return getPublishedEvents().find((event) => event.id === id)
}
